import { useEstabsContext } from '../../../../contexts/EstabsContext';
import { dateTimeErrorMessage, calendarDate, calendarDisplayDate, zonedDate } from '../../../../utils/date-time';
import moment from 'moment';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../../../../contexts/AppContext';
import { UserService } from '../../../configuracoes/usuario/Service';
import { AtendentesService } from '../../../atendentes/Service';
import { HorariosAtendenteService } from '../../Service';
import { FilaEsperaService } from '../../../fila-espera/Service';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useFilterApp } from '../../../../contexts/FilterApp';
import { ServicosService } from '../../../servicos/Service';

const userService = new UserService();
const serviceAtendentes = new AtendentesService();
const horariosAtendenteService = new HorariosAtendenteService();
const servicosService = new ServicosService();
const filaEsperaService = new FilaEsperaService();

interface ClientData {
    value: any,
    label: any,
    email?: string,
    phoneNumber?: string,
}

interface Recorrencia {
    ativa: boolean;
    tipo: 'dias' | 'semanas' | 'meses';
    frequencia: number;
    diaSemana: string;
    modoMensal?: 'diaMes' | 'diaSemana';
    ocorrenciaMensal?: 'primeira' | 'segunda' | 'terceira' | 'quarta' | 'ultima';
    diaMes?: number;
    inicio: Date | null;
    fim: Date | null;
}

interface FormHorarioClienteParams {
    duration: number;
    setDuration: React.Dispatch<React.SetStateAction<number>>;
    observation: string;
    setObservation: React.Dispatch<React.SetStateAction<string>>;
    observationClient: string;
    setObservationClient: React.Dispatch<React.SetStateAction<string>>;
    valor: number;
    setValor: React.Dispatch<React.SetStateAction<number>>;
    priceManual: boolean;
    setPriceManual: React.Dispatch<React.SetStateAction<boolean>>;
    subscriptionCovered: boolean;
    date: moment.Moment;
    handlePreviousWeek: () => void;
    handleNextWeek: () => void;
    setDate: React.Dispatch<React.SetStateAction<moment.Moment>>;
    servico: any;
    setServico: React.Dispatch<React.SetStateAction<any>>;
    servicosExtras: any[];
    setServicosExtras: React.Dispatch<React.SetStateAction<any[]>>;
    data: any;
    setData: React.Dispatch<React.SetStateAction<any>>;
    atendente: any;
    setAtendente: React.Dispatch<React.SetStateAction<any>>;
    horario: number;
    setHorario: React.Dispatch<React.SetStateAction<number>>;
    showHorarios: boolean;
    setShowHorarios: React.Dispatch<React.SetStateAction<boolean>>;
    daysOpen: number;
    setDaysOpen: React.Dispatch<React.SetStateAction<number>>;
    clientId: string;
    setClientId: React.Dispatch<React.SetStateAction<string>>;
    clients: ClientData[] | [];
    loading: boolean;
    openConfirm: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setOpenConfirm: React.Dispatch<React.SetStateAction<boolean>>;
    submitForm: () => void;
    recorrencia: Recorrencia;
    setRecorrencia: React.Dispatch<React.SetStateAction<Recorrencia>>;
    recurrenceMode: boolean;
    setClients: any;
    hasSelectionChanged: boolean;
    setHasSelectionChanged: React.Dispatch<React.SetStateAction<boolean>>;
    selectedSlotDuration: number;
    allowServicesBeyondSelectedSlot: boolean;
    setAllowServicesBeyondSelectedSlot: React.Dispatch<React.SetStateAction<boolean>>;
    doc?: any;
    waitlistEntryId?: string;
    lockServico?: boolean;
    lockCliente?: boolean;
    lockAtendente?: boolean;
    attendantCount: number | null;
    setAttendantCount: React.Dispatch<React.SetStateAction<number | null>>;
    resetAttendant: () => void;
    canStepBack: boolean;
    stepBack: () => void;
}

const FormHorarioClienteContext = createContext<FormHorarioClienteParams | undefined>(undefined);

export const useFormHorarioClienteContext = () => {
    const context = useContext(FormHorarioClienteContext);
    if (!context) {
        throw new Error('useFormHorarioClienteContext must be used within a FormHorarioClienteProvider');
    }
    return context;
};

export const FormHorarioClienteProvider: React.FC<{
    doc?: any;
    callback?: (doc: any) => void;
    redirectOnCreate?: boolean;
    lockServico?: boolean;
    lockCliente?: boolean;
    lockAtendente?: boolean;
    waitlistEntryId?: string;
    recurrenceMode?: boolean;
    children: React.ReactNode;
}> = ({
    doc,
    children,
    callback,
    redirectOnCreate = true,
    lockServico = false,
    lockCliente = false,
    lockAtendente = false,
    waitlistEntryId,
    recurrenceMode = false,
}) => {
    const { estabSession } = useEstabsContext();
    const timezone = estabSession?.timezone;
    const navigate = useNavigate();
    const [duration, setDuration] = useState(0);
    const { attendantId } = useFilterApp();

    if (!doc) {
        doc = {
            atendente_id: attendantId,
        };
    }

    const [openConfirm, setOpenConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [daysOpen, setDaysOpen] = useState(0);

    const [date, setDate] = useState(moment(calendarDisplayDate(doc?.startAt, timezone)));

    const [horario, setHorario] = useState(0);
    const [showHorarios, setShowHorarios] = useState(false);
    const [observation, setObservation] = useState(doc?.observation || '');
    const [observationClient, setObservationClient] = useState(doc?.observationClient || '');
    const [valor, setValor] = useState(doc?.price || 0);
    const [priceManual, setPriceManual] = useState(false);
    const [subscriptionCovered, setSubscriptionCovered] = useState(false);
    const [pricesByServiceId, setPricesByServiceId] = useState<Record<string, number>>({});
    const [data, setData] = useState(doc?.startAt ? calendarDisplayDate(doc?.startAt, timezone) : {} as any);
    const [servico, setServico] = useState({} as any);
    const [servicosExtras, setServicosExtras] = useState<any[]>([]);
    const [clientId, setClientId] = useState<string>('');
    const [atendente, setAtendente] = useState({} as any);
    const [clients, setClients] = useState<ClientData[] | []>([]);
    const [hasSelectionChanged, setHasSelectionChanged] = useState(false);
    const selectedSlotDuration = useMemo(() => {
        if (doc?._id) return 0;
        const parsedDuration = Number(doc?.duration || 0);
        return parsedDuration > 0 && typeof doc?.startMinutes === 'number'
            ? parsedDuration
            : 0;
    }, [doc?._id, doc?.duration, doc?.startMinutes]);
    const hasDurationFromSelectedRange = selectedSlotDuration > 0;
    const [allowServicesBeyondSelectedSlot, setAllowServicesBeyondSelectedSlot] = useState(false);
    const [attendantCount, setAttendantCount] = useState<number | null>(null);

    const resetAttendant = () => {
        setAtendente({});
        setServico({} as any);
        setServicosExtras([]);
        setHorario(0);
        setShowHorarios(false);
    };

    // Etapas sem escolha real (profissional unico ou travado) sao puladas tambem ao voltar.
    const backTarget = openConfirm
        ? 'confirm'
        : showHorarios
          ? 'horario'
          : atendente?.uid && (attendantCount || 0) > 1 && !lockAtendente
            ? 'servico'
            : null;

    const stepBack = () => {
        if (backTarget === 'confirm') setOpenConfirm(false);
        else if (backTarget === 'horario') setShowHorarios(false);
        else if (backTarget === 'servico') resetAttendant();
    };

    const [recorrencia, setRecorrencia] = useState<Recorrencia>(() => {
        const inicio = moment(calendarDisplayDate(doc?.startAt || new Date(), timezone)).startOf('day');
        return {
            ativa: recurrenceMode,
            tipo: recurrenceMode ? 'semanas' : 'dias',
            frequencia: 1,
            diaSemana: '',
            modoMensal: 'diaSemana',
            ocorrenciaMensal: 'ultima',
            diaMes: inicio.date(),
            inicio: inicio.toDate(),
            fim: recurrenceMode ? inicio.clone().add(30, 'days').toDate() : inicio.toDate(),
        };
    });

    const handlePreviousWeek = () => {
        setDate((prevDate) => {
            return moment(prevDate).subtract(7, 'days');
        });
    };

    const handleNextWeek = () => {
        setDate((prevDate) => {
            return moment(prevDate).add(7, 'days');
        });
    };

    const fetchClient = async () => {
        try {
            setLoading(true);

            const { data } = await userService.getClients(
                undefined,
                undefined,
                atendente?.uid,
                true
            );
            const clientsOptions = data.map((client: any) => ({
                label: client.displayName,
                value: client.uid,
                email: client.email,
                phoneNumber: client.phoneNumber,
            }));

            setClients(clientsOptions);
        } catch (error) {
            console.error('Erro ao buscar clientes:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadAgendaConfig = async () => {
        if (!atendente?.uid) return;

        try {
            setLoading(true);

            const { data } = await serviceAtendentes.getAgendaDate(atendente.uid, date.format('YYYY-MM-DD'));

            // setDaysOpen(data.daysOpen - 1 || 0);
        } catch (e) {

        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClient();
        loadAgendaConfig();

        if (!!doc?.duration) {
            setDuration(doc.duration);
        }

        if (!!doc?.startMinutes) {
            setHorario(doc.startMinutes);
        }

        if (doc?.clientId) {
            setClientId(doc.clientId);
        }
    }, [atendente]);

    useEffect(() => {
        if (!servico?._id) return;
        if (hasDurationFromSelectedRange && !allowServicesBeyondSelectedSlot) return;

        if (!doc?._id || hasSelectionChanged) {
            const extrasDuration = servicosExtras.reduce(
                (total, extra) => total + Number(extra?.duration || 0),
                0
            );
            setDuration(Number(servico?.duration || 0) + extrasDuration);
        }
    }, [
        servico,
        servicosExtras,
        hasSelectionChanged,
        hasDurationFromSelectedRange,
        allowServicesBeyondSelectedSlot,
    ]);

    useEffect(() => {
        if (!hasDurationFromSelectedRange) {
            setAllowServicesBeyondSelectedSlot(false);
            return;
        }

        if (!allowServicesBeyondSelectedSlot) {
            setDuration(selectedSlotDuration);
        }
    }, [hasDurationFromSelectedRange, allowServicesBeyondSelectedSlot, selectedSlotDuration]);

    const extrasIdsKey = useMemo(() => {
        return servicosExtras
            .map((extra: any) => String(extra?._id || ''))
            .filter(Boolean)
            .sort()
            .join('|');
    }, [servicosExtras]);

    useEffect(() => {
        if (!servico?._id || !data || !atendente?.uid) return;

        const dateValue = data instanceof Date ? data : new Date(data);
        if (Number.isNaN(dateValue.getTime())) return;

        const loadPrices = async () => {
            try {
                const servicosIds = [
                    servico?._id,
                    ...servicosExtras.map((extra: any) => extra?._id),
                ].filter(Boolean);

                if (!servicosIds.length) return;

                const response = await servicosService.resolvePrices({
                    date: calendarDate(dateValue),
                    attendantId: atendente?.uid,
                    servicosIds,
                });

                setPricesByServiceId(response?.data || {});
            } catch (error) {
                setPricesByServiceId({});
            }
        };

        loadPrices();
    }, [servico?._id, extrasIdsKey, atendente?.uid, data]);

    useEffect(() => {
        if (!servico?._id) return;
        if (priceManual || subscriptionCovered) return;
        if (doc?._id && !hasSelectionChanged) return;

        const servicosIds = [
            servico?._id,
            ...servicosExtras.map((extra: any) => extra?._id),
        ].filter(Boolean);

        if (!servicosIds.length) return;

        const totalFromRules = servicosIds.reduce((total, id) => {
            const price = pricesByServiceId?.[id];
            return total + Number(price || 0);
        }, 0);

        if (Object.keys(pricesByServiceId || {}).length) {
            setValor(totalFromRules);
            return;
        }

        const extrasPrice = servicosExtras.reduce(
            (total, extra) => total + Number(extra?.price || 0),
            0
        );
        setValor(Number(servico?.price || 0) + extrasPrice);
    }, [
        servico,
        servicosExtras,
        pricesByServiceId,
        priceManual,
        subscriptionCovered,
        hasSelectionChanged,
        doc?._id,
    ]);

    // Previa do valor com assinatura: o servidor aplica a mesma regra da criacao.
    useEffect(() => {
        const servicoId = servico?._id;
        let localDate = '';
        try {
            localDate = data ? calendarDate(data) : '';
        } catch {
            localDate = '';
        }
        if (!clientId || !servicoId || !horario || !localDate) {
            setSubscriptionCovered(false);
            return;
        }
        let active = true;
        horariosAtendenteService
            .getPrecoPreview({
                clientId,
                servicoId,
                servicosExtrasIds: extrasIdsKey ? extrasIdsKey.split('|') : [],
                localDate,
                startMinutes: horario,
            })
            .then(({ data: preview }) => {
                if (!active) return;
                const covered = !!preview?.covered;
                setSubscriptionCovered(covered);
                if (covered) {
                    setPriceManual(false);
                    setValor(Number(preview.chargedTotal ?? 0));
                }
            })
            .catch(() => {
                if (active) setSubscriptionCovered(false);
            });
        return () => {
            active = false;
        };
    }, [clientId, servico?._id, extrasIdsKey, data, horario]);

    useEffect(() => {
        if (!priceManual) return;
        setPriceManual(false);
    }, [servico?._id, extrasIdsKey]);

    const submitForm = async () => {
        const agendamentoData = {
            _id: doc?._id,
            observation,
            observationClient,
            valor,
            priceManual,
            startMinutes: horario,
            localDate: calendarDate(data),
            servicoId: servico._id,
            servicosExtrasIds: servicosExtras.map((extra: any) => extra._id),
            attendantId: atendente.uid,
            clientId,
            recorrencia: { ...recorrencia, inicio: recorrencia.inicio ? calendarDate(recorrencia.inicio) : undefined, fim: recorrencia.fim ? calendarDate(recorrencia.fim) : undefined, ativa: recurrenceMode },
            duration,
        };

        try {
            setLoading(true);

            if (waitlistEntryId) {
                await filaEsperaService.assign(waitlistEntryId, {
                    attendantId: atendente.uid,
                    localDate: agendamentoData.localDate,
                    startMinutes: horario,
                    observation,
                    valor,
                    priceManual,
                });
                successSnackbar('Agendamento criado com sucesso!');
            } else if (doc?._id) {
                await horariosAtendenteService.editAgendamento(agendamentoData);
                successSnackbar('Agendamento atualizado com sucesso!');
            } else {
                await horariosAtendenteService.createAgendamento(agendamentoData);
                successSnackbar(recurrenceMode ? 'Recorrência criada com sucesso!' : 'Agendamento criado com sucesso!');
                if (redirectOnCreate) {
                    navigate('/');
                }
            }

            if (!!callback) callback(agendamentoData);
        } catch (error) {
            const message = error instanceof AxiosError ? error?.response?.data?.message : undefined;
            const timeError = dateTimeErrorMessage(message);
            if (timeError) { errorSnackbar(timeError); return; }
            if (message === 'conflictHours' || message === 'waitlistSlotUnavailable') {
                errorSnackbar('Este horário já está ocupado. Escolha outro horário disponível.');
                // Nova referencia de data faz a lista recarregar sem o horario que foi ocupado.
                setHorario(0);
                setData((current: any) => new Date(current));
            } else if (message === 'attendantServiceMismatch') {
                errorSnackbar('O profissional escolhido não realiza um ou mais serviços selecionados.');
            } else if (message === 'waitlistOutsidePreferredRange') {
                errorSnackbar('Horário fora da faixa de datas do cliente.');
            } else if (message === 'waitlistOutsideTimeWindow') {
                errorSnackbar('Horário fora da janela preferida do cliente.');
            } else if (message === 'waitlistAttendantMismatch') {
                errorSnackbar('Este cliente escolheu outro atendente.');
            } else {
                errorSnackbar('Não foi possível salvar o agendamento. Tente novamente em alguns instantes.');
            }
        } finally {
            setLoading(false);
        }

        setOpenConfirm(false);
    };

    return (
        <FormHorarioClienteContext.Provider
            value={{
                attendantCount,
                setAttendantCount,
                resetAttendant,
                canStepBack: !!backTarget,
                stepBack,
                observation,
                setObservation,
                observationClient,
                setObservationClient,
                valor,
                setValor,
                date,
                handlePreviousWeek,
                handleNextWeek,
                setDate,
                servico,
                setServico,
                servicosExtras,
                setServicosExtras,
                setLoading,
                setClients,
                data,
                duration,
                setDuration,
                setData,
                atendente,
                setAtendente,
                horario,
                subscriptionCovered,
                showHorarios,
                setShowHorarios,
                daysOpen, setDaysOpen,
                setHorario,
                clientId,
                setClientId,
                clients,
                loading,
                doc,
                priceManual,
                setPriceManual,
                openConfirm,
                setOpenConfirm,
                submitForm,
                recorrencia,
                setRecorrencia,
                recurrenceMode,
                hasSelectionChanged,
                setHasSelectionChanged,
                selectedSlotDuration,
                allowServicesBeyondSelectedSlot,
                setAllowServicesBeyondSelectedSlot,
                waitlistEntryId,
                lockServico,
                lockCliente,
                lockAtendente,
            }}
        >
            {children}
        </FormHorarioClienteContext.Provider>
    );
};
